/**
 * Small in-memory cache for organization-scoped reference-data reads that are
 * stable (or only slowly changing) for the lifetime of a page load.
 *
 * Why this exists: screens like the picking order list refetch the same
 * reference endpoints (cancellation reasons, organization shipping methods,
 * coupon codes) on *every* mount. With list -> show -> list navigation that is
 * thousands of redundant requests per day.
 *
 * Correctness:
 *  - Every entry is keyed by the current `organizationId` (the same value the
 *    fetch wrapper appends to every request). Switching organization triggers a
 *    full page reload (each org lives on its own subdomain), which drops this
 *    module state entirely, so one org can never read another org's cached
 *    data. Keying by org is belt-and-suspenders on top of that.
 *  - Each entry carries a TTL so slowly-changing / admin-editable data becomes
 *    fresh again without requiring a manual reload.
 *  - The resolved *promise* is cached (not just its value), which also
 *    deduplicates concurrent in-flight requests. A rejected/aborted request is
 *    evicted so the next call retries.
 *
 * This deliberately caches at the call site rather than inside the shared
 * `api/*` functions, because those same functions back CRUD settings screens
 * that must always read fresh data after a mutation.
 */

// TTL for org-stable reference data that only changes via a settings screen
// (cancellation reasons, organization shipping methods). Long enough to absorb
// rapid list<->show navigation, short enough that an admin edit shows up
// without a hard reload.
export const ORG_REFERENCE_TTL_MS = 15 * 60 * 1000;

// TTL for derived, slowly-growing facets (coupon codes seen across the org's
// orders, distinct line-item shipping methods). Shorter, because the underlying
// set grows as new orders arrive.
export const FACET_TTL_MS = 5 * 60 * 1000;

const store = new Map();

function organizationScope() {
    try {
        return localStorage.getItem('organizationId') || 'no-org';
    } catch (e) {
        return 'no-org';
    }
}

/**
 * Return a cached promise for the given key, or invoke `producer` on a miss.
 *
 * @param {string}   namespace logical bucket, e.g. 'cancellationReasons'
 * @param {string}   key       variant key within the bucket (params, ids, ...)
 * @param {number}   ttlMs     max age (ms) before the entry is considered stale
 * @param {function} producer  () => Promise, invoked only on a cache miss
 * @returns {Promise}
 */
export function cachedRequest(namespace, key, ttlMs, producer) {
    const cacheKey = `${namespace}::${organizationScope()}::${key}`;
    const cached = store.get(cacheKey);

    if (cached && (Date.now() - cached.storedAt) < ttlMs) {
        return cached.promise;
    }

    const promise = producer();

    promise.catch(() => {
        // Evict failed/aborted requests so a later mount can retry.
        if (store.get(cacheKey)?.promise === promise) {
            store.delete(cacheKey);
        }
    });

    store.set(cacheKey, {promise, storedAt: Date.now()});

    return promise;
}

/**
 * Drop every cached entry. Org switch and logout are full page navigations that
 * already discard this module state; this is exposed for explicit invalidation
 * (e.g. after a reference-data mutation) and for tests.
 */
export function clearReferenceDataCache() {
    store.clear();
}
